import { Api } from "../../Api";
import { ITypedEvent, TypedEvent } from "../../TypedEvent";
import { FieldChangeResultType, FieldChangeResult } from "../FieldChangeResult";
import { DevicesService } from "./DevicesService";
import { IDeviceTaskTypeInfo, IDeviceResponseTypeInfo, IDevice } from './IDevice';
import { ChannelConfigurations, IDeviceChannel, Roles, TaskExecution } from './IDeviceChannel';
import { IDevicesService } from "./IDevicesService";
import { IDeviceState, IDeviceStateData } from "./IDeviceState";
import { IDeviceTask } from "./IDeviceTask";
import { DeviceTaskExecutionResult } from "./TaskExecutionResult";
import { IExalusConnectionService } from '../IExalusConnectionService';
import { ExalusConnectionService } from "../ExalusConnectionService";
import { DataFrame, Method, Status } from "../../DataFrame";
import { DependencyContainer } from '../../DependencyContainer';
import { DeviceTask } from "./Tasks/DeviceTask";
import { Device } from "./Device";
import { LoggerService } from "../Logging/LoggerService";
import { IControllerConfigurationService } from "../Controller/IControllerConfigurationService";
import { ControllerConfigurationService } from "../Controller/ControllerConfigurationService";
import { WebApiCacheService } from "../WebApi/WebApiCacheService";
import { IWebApiCacheService } from "../WebApi/IWebApiCacheService";

export class DeviceChannel implements IDeviceChannel {
    private _device: IDevice = new Device();
    private _number: number = 0;
    private _name: string = "";
    private _iconName: string = "";
    private _channelId: string = "";
    private _mode: ChannelConfigurations = ChannelConfigurations.Default;
    private _channelGroups: string[] = [];
    private _isHidden: boolean = false;
    private _availableTasksTypes: IDeviceTaskTypeInfo[] = [];
    private _availableResponseTypes: IDeviceResponseTypeInfo[] = [];
    private static _devicesService: DevicesService | null = null;
    private _customData: Record<string, string> = {};
    private _roles: Roles[] = [];
    private _configurations: Record<string, string> = {};
    public CustomDataAndRolesSupported: boolean = false;
    private _onTasksExecutionChangedEvent: ITypedEvent<TaskExecution> = new TypedEvent<TaskExecution>();
    private _onChannelStateChangedEvent: ITypedEvent<IDeviceState<IDeviceStateData>> = new TypedEvent<IDeviceState<IDeviceStateData>>();
    private _onChannelStateRefreshedOrChangedEvent: ITypedEvent<IDeviceState<IDeviceStateData>> = new TypedEvent<IDeviceState<IDeviceStateData>>();

    constructor() {
        if (DeviceChannel._devicesService == null)
            DeviceChannel._devicesService = Api.Get<DevicesService>(DevicesService.ServiceName);
    }

    SetCustomDataAsync(dataId: string, data: string): Promise<FieldChangeResult>;
    SetCustomDataAsync(data: Array<{ dataId: string; data: string; }>): Promise<FieldChangeResult>;
    SetCustomDataAsync(dataIdOrEntries: unknown, dataValue?: unknown): Promise<FieldChangeResult> {
        const current = this.GetChannelConfigurationData();


        if (Array.isArray(dataIdOrEntries)) {
            for (const entry of dataIdOrEntries) {
                current.CustomData[entry.dataId] = entry.data;
                this._customData[entry.dataId] = entry.data;
            }   
        }
        else {
            current.CustomData[dataIdOrEntries as string] = dataValue as string;
            this._customData[dataIdOrEntries as string] = dataValue as string;
        }   
        
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = current;
        return this.ChangeConfigurationAsync(conf);
    }

    OnChannelStateChangedEvent(): ITypedEvent<IDeviceState<IDeviceStateData>> {
        return this._onChannelStateChangedEvent;
    }

    OnChannelStateRefreshedOrChangedEvent(): ITypedEvent<IDeviceState<IDeviceStateData>> {
        return this._onChannelStateRefreshedOrChangedEvent;
    }

    
    get Configurations(): Record<string, string>
    {
        return this._configurations;
    }

    set Configurations(value: Record<string, string>)
    {
        this._configurations = value;
    }

    IsCustomDataAndRolesSupported(): boolean {
        return this.CustomDataAndRolesSupported;
    }

    get CustomData(): Record<string, string> {
        return this._customData;
    }

    set CustomData(value: Record<string, string>) {
        this._customData = value;
    }

    get Roles(): Roles[] {
        return this._roles;
    }

    set Roles(value: Roles[]) {
        this._roles = value;
    }

    get ChannelId(): string {
        return this._channelId;
    }

    SetDevice(device: IDevice): void {
        this._device = device;
        this._channelId = `${device.Guid}_${this.Number}`;
        
        this._device.OnDeviceStateChangedEvent().Subscribe((state) => {
            if (state.Data.Channel == this.Number)
                (this._onChannelStateChangedEvent as TypedEvent<IDeviceState<IDeviceStateData>>).Invoke(state);
        });

        this._device.OnDeviceStateRefreshedOrChangedEvent().Subscribe((state) => {
            if (state.Data.Channel == this.Number)
                (this._onChannelStateRefreshedOrChangedEvent as TypedEvent<IDeviceState<IDeviceStateData>>).Invoke(state);
        });
    }

    GetDevice(): IDevice {
        return this._device;
    }



    RemoveCustomDataAsync(dataId: string): Promise<FieldChangeResult> {
        const current = this.GetChannelConfigurationData();
        delete current.CustomData[dataId];
        delete this._customData[dataId];
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = current;
        return this.ChangeConfigurationAsync(conf);
    }

    GetChannelConfigurationData(): ChannelConfigurationData {
        const data = new ChannelConfigurationData();
        data.Channel = this._number;
        data.ChannelName = this._name;
        data.Configuration = this._mode;
        data.CustomData = this._customData;
        data.GroupsGuids = this._channelGroups;
        data.Hidden = this._isHidden;
        data.IconName = this._iconName;
        return data;
    }

    HideAsync(): Promise<FieldChangeResult> {
        const data = this.GetChannelConfigurationData();
        this._isHidden = true;
        data.Hidden = true;
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = data;
        return this.ChangeConfigurationAsync(conf);
    }

    ShowAsync(): Promise<FieldChangeResult> {
        const data = this.GetChannelConfigurationData();
        data.Hidden = false;
        this._isHidden = false;
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = data;
        return this.ChangeConfigurationAsync(conf);
    }

    ChangeNameAsync(name: string): Promise<FieldChangeResult> {
        const data = this.GetChannelConfigurationData();
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = data;
        data.ChannelName = name;
        this._name = name;
        return this.ChangeConfigurationAsync(conf);
    }

    ChangeIconNameAsync(name: string): Promise<FieldChangeResult> {
        const data = this.GetChannelConfigurationData();
        data.IconName = name;
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = data;
        this._iconName = name;
        return this.ChangeConfigurationAsync(conf);
    }

    ChangeGroupsAsync(groups: string[]): Promise<FieldChangeResult> {
        const data = this.GetChannelConfigurationData();
        data.GroupsGuids = groups;
        const conf = new DeviceChannelConfiguration();
        conf.DeviceGuid = this._device.Guid;
        conf.ChannelConfiguration = data;
        this._channelGroups = groups;
        return this.ChangeConfigurationAsync(conf);
    }

    async ChangeConfigurationAsync(configuration: DeviceChannelConfiguration): Promise<FieldChangeResult> {
        try {
            const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName)
                .SendAndWaitForResponseAsync(new DeviceChannelConfigurationRequest(configuration), 20000, false);
                DependencyContainer.Log?.Error("Device", `EXIT CONFIGURATION MODE WILL BE FIRED! ChangeConfigurationAsync()`);
                await Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName).ExitConfigurationModeAsync();

            switch (result.Status) {
                case Status.OK:
                    this._name = configuration.ChannelConfiguration.ChannelName;
                    this._iconName = configuration.ChannelConfiguration.IconName;
                    this._channelGroups = configuration.ChannelConfiguration.GroupsGuids;
                    this._isHidden = configuration.ChannelConfiguration.Hidden;
                    this._mode = configuration.ChannelConfiguration.Configuration;
                    this._customData = configuration.ChannelConfiguration.CustomData;
                    this._roles = configuration.ChannelConfiguration.Roles;
                    this._configurations = configuration.ChannelConfiguration.Configurations;

                    return new FieldChangeResult(FieldChangeResultType.Changed, "");

                case Status.WrongData:
                    switch (result.Data) {
                        case "DeviceNotFound":
                            DependencyContainer.Log?.Error(`Failed to change channel configuration, referenced device does not exist!`);
                            return new FieldChangeResult(FieldChangeResultType.WrongData, "DeviceNotFound");
                    }
                    break;

                case Status.FatalError:
                    return new FieldChangeResult(FieldChangeResultType.UnknownError, "");
            }
        }
        catch (ex: any) {
            if ((ex as Error).message.includes("timeout"))
                return new FieldChangeResult(FieldChangeResultType.ControllerResponseTimeout, "");
        }

        return new FieldChangeResult(FieldChangeResultType.Unknown, "");
    }

    get Number(): number {
        return this._number;
    }

    set Number(value: number) {
        this._number = value;
    }

    get Name(): string {
        return this._name;
    }
    set Name(value: string) {
        this._name = value;
    }

    get IconName(): string {
        return this._iconName;
    }

    set IconName(value: string) {
        this._iconName = value;
    }

    get States(): IDeviceState<IDeviceStateData>[] {

        if (LoggerService.DoesThrowStackTrace)
            return (Api.Get<DevicesService>(DevicesService.ServiceName)).GetDeviceStates(this.GetDevice())
                .where(a => a.Channel == this.Number)
                .selectMany(a=>a.States)
                .toArray()
        else

            return (DeviceChannel._devicesService as DevicesService)
                .GetDeviceStates(this.GetDevice())
                .where(a => a.Channel == this.Number)
                .selectMany(a=>a.States)
                .toArray()
    }

    get Mode(): ChannelConfigurations {
        return this._mode;
    }

    set Mode(value: ChannelConfigurations) {
        this._mode = value;
    }

    get ChannelGroups(): string[] {
        return this._channelGroups;
    }

    set ChannelGroups(value: string[]) {
        this._channelGroups = value;
    }

    get IsHidden(): boolean {
        return this._isHidden;
    }

    set IsHidden(value: boolean) {
        this._isHidden = value;
    }

    get AvailableTaskTypes(): IDeviceTaskTypeInfo[] {
        return this._availableTasksTypes;
    }

    set AvailableTaskTypes(value: IDeviceTaskTypeInfo[]) {
        this._availableTasksTypes = value;
    }

    get AvailableResponseTypes(): IDeviceResponseTypeInfo[] {
        return this._availableResponseTypes;
    }

    set AvailableResponseTypes(value: IDeviceResponseTypeInfo[]) {
        this._availableResponseTypes = value;
    }

    OnTasksExecutionChangeEvent(): ITypedEvent<TaskExecution> {
        return this._onTasksExecutionChangedEvent;
    }

    ExecuteTaskAsync(task: IDeviceTask): Promise<DeviceTaskExecutionResult> {
        if (this._device != null) {
            (task as DeviceTask).Channel = this.Number;
            (task as DeviceTask).DeviceGuid = this._device.Guid;
            return (DeviceChannel._devicesService as DevicesService).ExecuteDeviceTaskAsync(this._device, task);
        }
        else
            return Promise.resolve(DeviceTaskExecutionResult.Failed);
    }
}

export class DeviceChannelConfiguration {
    DeviceGuid: string = "";
    ChannelConfiguration: ChannelConfigurationData = new ChannelConfigurationData();
}

export class ChannelConfigurationData {
    Channel: number = 0;
    ChannelName: string = "";
    Configuration: ChannelConfigurations = ChannelConfigurations.Default;
    GroupsGuids: string[] = [];
    IconName: string = "";
    Hidden: boolean = false;
    Configurations: Record<string, string> = {};
    CustomData: Record<string, string> = {};
    Roles: Roles[] = [];
}

class DeviceChannelConfigurationRequest extends DataFrame<DeviceChannelConfiguration>
{
    constructor(configuration: DeviceChannelConfiguration) {
        super();
        this.Resource = "/devices/device/channel/configuration";
        this.Method = Method.Put;
        this.Data = configuration;
    }
}